<script setup>
import { computed } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import {
  barColors,
  categories,
  chartColors,
  customLabels,
  expandedCategories,
} from './personalFiveChart'

const props = defineProps({
  chartData: Object,
  chartType: String, // 'total', 'jijangFive'
})

const baseBarData = categories.map((key) => {
  const baseData =
    props.chartType === 'total'
      ? props.chartData.기본오행비율
      : props.chartType === 'jijangFive'
      ? props.chartData.기본비율
      : null

  return baseData
    ? {
        x: categories.indexOf(key),
        y: baseData[key],
        fillColor: barColors[key],
      }
    : { x: categories.indexOf(key), y: 0 }
})

const yinYangLine =
  props.chartType === 'total'
    ? categories.map((key) => ({
        x: categories.indexOf(key),
        y: props.chartData.음양오행비율[key] || 0,
      }))
    : []

const johooLine =
  props.chartType === 'total'
    ? categories.map((key) => ({
        x: categories.indexOf(key),
        y: props.chartData.조후오행비율[key] || 0,
      }))
    : []

const jijangLine =
  props.chartType === 'jijangFive'
    ? categories.map((key) => ({
        x: categories.indexOf(key),
        y: props.chartData.지장비율[key] || 0,
      }))
    : []

const series = computed(() => {
  const result = [{ name: '기본오행분포', type: 'column', data: baseBarData }]

  if (props.chartType === 'total') {
    result.push(
      { name: '음양오행분포', type: 'line', data: yinYangLine },
      { name: '조후오행분포', type: 'line', data: johooLine },
    )
  } else if (props.chartType === 'jijangFive') {
    result.push({ name: '지장오행분포', type: 'line', data: jijangLine })
  }

  return result
})

const chartOptions = computed(() => {
  const activeColors = chartColors.slice(0, series.value.length)

  return {
    chart: {
      type: 'line',
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false },
      parentHeightOffset: 0,
      width: '100%',
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusWhenStacked: 'last',
        columnWidth: '40px',
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: false,
      offsetY: -12,
      style: {
        colors: activeColors,
      },
      background: {
        enabled: false,
        foreColor: '#000',
        fill: '#fff',
        padding: 4,
        opacity: 1,
        borderColor: 'transparent',
        dropShadow: {
          enabled: false,
        },
      },
      // formatter: (val, { seriesIndex }) => {
      //   if (seriesIndex === 0) return `${val}%`
      //   return ''
      // },
      formatter: (val, { seriesIndex }) => {
        if (props.chartType === 'total') {
          return seriesIndex === 0 ? `${val}%` : ''
        } else if (props.chartType === 'jijangFive') {
          return seriesIndex === 1 ? `${val}%` : ''
        }
        return ''
      },
    },
    colors: activeColors,
    xaxis: {
      // categories: customLabels,
      axisTicks: { show: false },
      axisBorder: { show: false },
      tickPlacement: 'on',
      labels: {
        style: {
          colors: expandedCategories.map((key) => barColors[key]),
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontWeight: '500',
        },
        formatter: (val) => {
          return customLabels[val] || ''
        },
      },
      type: 'numeric',
      min: -0.5, // 좌우 여백
      max: 4.5,
      tickAmount: 10,
    },
    yaxis: {
      tickAmount: 4,
      labels: {
        padding: 8,
        formatter: (val) => {
          if (props.chartType === 'jijangFive' || val === 0) return Math.round(val)
          return ''
        },
      },
    },
    legend: {
      position: 'bottom',
      onItemClick: {
        toggleDataSeries: false,
      },
    },
    grid: {
      padding: {
        top: 4,
        bottom: 16,
        left: -4,
      },
    },
    tooltip: { 
      enabled: true,
      y: {
        formatter: (val) => val.toString(),
      },
    },
    stroke: {
      // 'column', 'line', 'line' 순
      width: [0, 2, 2].slice(0, series.value.length),
      curve: ['smooth', 'smooth', 'smooth'].slice(0, series.value.length),
      colors: activeColors,
    },
    markers: {
      size: 4,
      strokeWidth: 2,
      fillOpacity: 1,
      colors: ['#fff'],
      strokeColors: activeColors,
    },
  }
})

const updateChartStyles = () => {
  nextTick(() => {
    // 막대 차트 '%' 레이블
    const labels = document.querySelectorAll('.apexcharts-data-labels text')
    // 차트 툴팁 아이콘
    const tooltipMarkers = document.querySelectorAll('.apexcharts-tooltip-marker')
    // 범례 아이콘
    const legendMarkers = document.querySelectorAll('.apexcharts-legend-marker')
    // 범례 텍스트
    const legendText = document.querySelectorAll('.apexcharts-legend-text')

    const urls = [
      '/images/chart/bar-chart__legend.svg',
      '/images/chart/line-chart__legend-orange.svg',
      '/images/chart/line-chart__legend-blue.svg',
    ]

    labels.forEach((label, index) => {
      const key = categories[index]
      label.setAttribute('fill', barColors[key])
      label.style.fontWeight = '700'
    })
    const legendMarkerSize = window.innerWidth < 500 ? '16px' : '22px'
    legendMarkers.forEach((marker, index) => {
      marker.style.background = `url(${urls[index]}) no-repeat center/contain`
      marker.style.borderRadius = '0'
      marker.style.width = legendMarkerSize
      marker.style.height = '12px'
    })
    tooltipMarkers.forEach((marker, index) => {
      marker.style.background = `url(${urls[index]}) no-repeat center/contain`
      marker.style.borderRadius = '0'
      marker.style.width = '22px'
      marker.style.height = '12px'
    })
    const fontSize = window.innerWidth < 500 ? '12px' : '14px'
    legendText.forEach((el) => {
      el.style.color = '#333'
      el.style.fontSize = fontSize
      el.style.fontFamily = 'Pretendard'
      el.style.fontWeight = '400'
      el.style.lineHeight = '18px'
    })
  })
}

onMounted(updateChartStyles)
</script>

<template>
  <div class="chart-container">
    <VueApexCharts type="line" height="300" :options="chartOptions" :series="series" />
  </div>
</template>

<style scoped lang="scss">
.chart-container {
  width: 100%;
  max-width: 500px;
  overflow: hidden;
}

.chart-container .apexcharts-canvas {
  max-width: 100% !important;
  width: 100% !important;
}

.apexcharts-tooltip {
  border-radius: 5px;
}

.apexcharts-tooltip-marker {
  background-color: transparent !important;
}

@media (max-width: 768px) {
  .chart-container {
    max-width: 360px;
  }
}
</style>
